import OriginalCarImageIcon from "@/assets/illustrations/OriginalCarImageIcon.svg";
import i18n from "@/lang/i18n";
import { useDriverSelfCheckIn } from "@/hooks";
import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "../ui/button";
import { API_BASE_URL } from "@/constants";
import MiscellaneousService from "@/services/api/MiscellaneousService";
import LoginFlowService from "@/services/api/LoginFlowService";
import { OcrType } from "@/central-atoms/ocr/enums";
import { Oval } from "react-loader-spinner";
import { useTheme } from "@/themeConfigs/ThemeContext";
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from "@mui/material";
import CameraIcon from '@mui/icons-material/PhotoCamera';
import ErrorIcon from '@mui/icons-material/Error';
import FlipCameraAndroidIcon from '@mui/icons-material/FlipCameraAndroid';
import Webcam from 'react-webcam';

interface LoginTokenCarDetailsSelfCheckInProps {
  vrn: string;
  onOCRComplete: (odo: number, soc: number) => void;
}

const LoginTokenCarDetailsSelfCheckIn: React.FC<LoginTokenCarDetailsSelfCheckInProps> = ({ vrn, onOCRComplete }) => {
  const { theme } = useTheme();
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<"user" | "environment">("environment");
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessingOCR, setIsProcessingOCR] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | undefined>(undefined);
  const [ocrResults, setOcrResults] = useState<{ odo: number; soc: number } | null>(null);
  
  const webcamRef = useRef<Webcam>(null);

  // Get device type
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const videoConstraints = {
    width: isMobile ? { ideal: 1080 } : 1280,
    height: isMobile ? { ideal: 1920 } : 720,
    facingMode: facingMode,
    aspectRatio: 4/3
  };

  // Remove device-specific aspect ratio
  const cameraContainerStyle = "aspect-[4/3]";

  // Check if device has multiple cameras
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);

  // Check for available cameras when component mounts
  useEffect(() => {
    const checkCameras = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        setHasMultipleCameras(videoDevices.length > 1);
      } catch (error) {
        console.error('Error checking cameras:', error);
        setHasMultipleCameras(false);
      }
    };
    
    checkCameras();
  }, []);

  const startCamera = async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(track => track.stop());
      setIsCameraActive(true);
    } catch (error) {
      console.error('Camera permission error:', error);
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        setCameraError(i18n.t('camera_permission_denied'));
      } else if (error instanceof DOMException && error.name === 'NotFoundError') {
        setCameraError(i18n.t('no_camera_found'));
      } else {
        setCameraError(i18n.t('camera_error'));
      }
    }
  };

  const stopCamera = () => {
    setIsCameraActive(false);
    setCameraError(null);
  };

  const switchCamera = async () => {
    if (webcamRef.current && webcamRef.current.video) {
      const stream = webcamRef.current.video.srcObject as MediaStream;
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    }
    setFacingMode(prev => prev === "user" ? "environment" : "user");
  };

  const handleCameraError = (error: string | DOMException) => {
    console.error('Camera error:', error);
    setCameraError('Failed to access camera. Please check permissions and try again.');
    setIsCameraActive(false);
  };

  const capturePhoto = useCallback(async () => {
    try {
      const imageSrc = webcamRef.current?.getScreenshot();
      if (!imageSrc) {
        throw new Error('Failed to capture photo');
      }

      // Convert image to file
      const response = await fetch(imageSrc);
      const blob = await response.blob();
      const file = new File([blob], 'camera-photo.jpg', { type: 'image/jpeg' });
      
      // Show the captured image
      setUploadedImage(URL.createObjectURL(file));
      setIsCameraActive(false);

      // Process the image
      await handleImageUpload(file);
    } catch (error) {
      console.error('Error capturing photo:', error);
      setCameraError('Failed to capture photo. Please try again.');
    }
  }, [webcamRef]);

  const handleImageUpload = async (file: File) => {
    try {
      setIsUploading(true);
      setOcrResults(null);
      
      const token = localStorage.getItem("auth_token");
      if (!token) {
        throw new Error('No auth token found');
      }

      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await MiscellaneousService.uploadFile(formData, token);
      const mediaId = uploadResponse.data.data.media_ids[0];

      setUploadedImage(URL.createObjectURL(file));
      setIsUploading(false);

      setIsProcessingOCR(true);
      const ocrResponse = await LoginFlowService.fetchOCRDetailsFromImage({
        media_id: mediaId,
        media_type: OcrType.dashboard
      }, token);

      const odo = Number(ocrResponse.data.data.odo);
      const soc = Number(ocrResponse.data.data.soc);
      setOcrResults({ odo, soc });
    } catch (error) {
      console.error('Error during upload/OCR:', error);
      setCameraError('Failed to process image. Please try again.');
    } finally {
      setIsUploading(false);
      setIsProcessingOCR(false);
    }
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setUploadedImage(undefined);
    setOcrResults(null);
    stopCamera();
  };

  const handleConfirm = () => {
    if (ocrResults) {
      onOCRComplete(ocrResults.odo, ocrResults.soc);
    }
  };

  return (
    <>
      <div className="px-[26px] relative z-10 pt-[26px]">
        <div className="shadow-[0px_2px_0px_#3ebec8] rounded-[30px] box-border border-2 border-solid border-primary flex justify-center items-center pt-4 bg-white">
          <div className="flex flex-col items-center w-full">
            <h5 className="text-secondary text-[20px] font-semibold mb-3">{i18n.t('driver_check_in')}</h5>
            <img src={OriginalCarImageIcon} className="w-60" />
            <div className="flex flex-col items-center">
              <h5 className="text-secondary font-semibold text-[20px] leading-[40px]">{vrn}</h5>
            </div>
            {ocrResults && (
              <div className="grid grid-cols-2 gap-4 w-full border-primary border-t-[1px]">
                <div className="flex flex-col justify-center ml-5 pr-[23px] border-primary border-r-[1px] pt-4 pb-2">
                  <span>
                    <p className="text-secondary text-[14px] inline mr-[4px]">{i18n.t('odometer')}</p>
                  </span>
                  <h5 className="text-primary font-semibold text-[20px]">{ocrResults.odo.toFixed(0)} {i18n.t('km')}</h5>
                </div>
                <div className="flex flex-col items-center justify-center mr-5 pt-4 pb-2">
                  <span>
                    <p className="text-secondary text-[14px] inline mr-[4px]">{i18n.t('soc')}</p>
                  </span>
                  <h5 className="text-primary font-semibold text-[20px]">{ocrResults.soc}%</h5>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-10">
          {isCameraActive ? (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col">
                <div className={`w-full ${cameraContainerStyle} bg-black relative`}>
                  <Webcam
                    ref={webcamRef}
                    audio={false}
                    screenshotFormat="image/jpeg"
                    videoConstraints={videoConstraints}
                    onUserMediaError={handleCameraError}
                    className="w-full h-full object-cover"
                    mirrored={facingMode === "user"}
                  />
                </div>
                <div className="bg-black">
                  <div className="flex items-center justify-center relative px-8 py-4">
                    <Button
                      variant="ghost"
                      onClick={capturePhoto}
                      disabled={isUploading || isProcessingOCR}
                      className="bg-white hover:bg-gray-100 rounded-full p-0 w-12 h-12 flex items-center justify-center"
                    >
                      {(isUploading || isProcessingOCR) ? (
                        <Oval
                          height={24}
                          width={24}
                          color={theme.colors.primary}
                          secondaryColor={theme.colors.secondary}
                          strokeWidth={4}
                          strokeWidthSecondary={2}
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-white border-2 border-gray-300"></div>
                      )}
                    </Button>
                    {hasMultipleCameras && (
                      <Button
                        variant="ghost"
                        onClick={switchCamera}
                        className="bg-gray-800/50 hover:bg-gray-700/50 rounded-full p-2 min-w-[40px] absolute left-0"
                      >
                        <FlipCameraAndroidIcon className="w-5 h-5 text-white" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : uploadedImage ? (
            <div className={`relative w-full ${cameraContainerStyle}`}>
              <div className="w-full h-full rounded-lg overflow-hidden border-2 border-primary">
                <img src={uploadedImage} alt="Captured dashboard" className="w-full h-full object-cover" />
              </div>
              <IconButton
                size="small" 
                style={{
                  position: 'absolute',
                  top: -15,
                  right: -15,
                  zIndex: 100,
                  borderRadius: '100%',
                  borderColor: '#3ebec8',
                  borderWidth: 1,
                  borderStyle: 'solid',
                  backgroundColor: 'white',
                }}
                onClick={handleCancel}
              >
                <CloseIcon />
              </IconButton>
            </div>
          ) : (
            <Button 
              variant="primary"
              size="lg"
              className="w-full"
              disabled={isUploading || isProcessingOCR}
              onClick={startCamera}
            >
              <CameraIcon className="mr-2" />
              {i18n.t('take_dashboard_pic')}
            </Button>
          )}

          {cameraError && (
            <div className="w-full p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600">
              <ErrorIcon fontSize="small" />
              <span>{cameraError}</span>
            </div>
          )}

          {(isUploading || isProcessingOCR) && (
            <div className="flex items-center gap-2 mt-5 mb-5">
              <Oval
                height={20}
                width={20}
                color={theme.colors.primary}
                ariaLabel="button-loading"
                secondaryColor={theme.colors.secondary}
                strokeWidth={4}
                strokeWidthSecondary={2}
              />
              {isUploading ? i18n.t('uploading_image') : i18n.t('processing_ocr')}
            </div>
          )}

            <Button
              className="mt-7 w-full"
              variant="secondary"
              size="lg"
              onClick={handleConfirm}
              disabled={isProcessingOCR || !ocrResults}
            >
              {i18n.t('confirm')}
            </Button>
        </div>
      </div>
    </>
  );
};

export default LoginTokenCarDetailsSelfCheckIn;
